import { useEffect, useMemo } from "react"
import { type UseQueryExecute, useQuery } from "urql"

import "../graphql/customerQuality.query"

import type {
  AssociatedTestTemplate,
  AssociatedTestTemplateListOutputFailure,
  AssociatedTestTemplateListOutputSuccess,
} from "@/graphql/codegen/graphql"
import { GetCustomerAssociatedTestTemplateListDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGetCustomerAssociatedTestTemplatesProps {
  productId: number
  companyId: string
  locationId: string
}

interface UseGetCustomerAssociatedTestTemplatesReturn {
  associatedTestTemplates: AssociatedTestTemplate[]
  isFetching: boolean
  refetch: UseQueryExecute
}

export const useGetCustomerAssociatedTestTemplates = ({
  productId,
  companyId,
  locationId,
}: UseGetCustomerAssociatedTestTemplatesProps): UseGetCustomerAssociatedTestTemplatesReturn => {
  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: GetCustomerAssociatedTestTemplateListDocument,
    variables: { productId, companyId, locationId },
    pause: !locationId,
  })
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  const associatedTestTemplates = useMemo(() => {
    if (!data) return []

    if (data.associatedTestTemplate.list.__typename === "AssociatedTestTemplateListOutputFailure" || error) {
      return []
    }

    return (data.associatedTestTemplate.list as AssociatedTestTemplateListOutputSuccess)
      .associatedTestTemplates as AssociatedTestTemplate[]
  }, [data, error])

  useEffect(() => {
    if (!data) return
    if (data.associatedTestTemplate.list.__typename === "AssociatedTestTemplateListOutputFailure" || error) {
      notify({
        message: "Failed to fetch customer associated test templates",
        content:
          error?.message ?? (data.associatedTestTemplate.list as AssociatedTestTemplateListOutputFailure).error.message,
        type: NotificationType.Error,
      })
    }
  }, [data, error, notify])

  return {
    associatedTestTemplates,
    isFetching: fetching,
    refetch: reexecuteQuery,
  }
}
